import { cn } from "@/lib/utils";
import type { RefObject } from "react";
import { DesignTokensPanelHeader } from "./DesignTokensPanelHeader";
import React from "react";

interface DesignTokensSkeletonProps {
  className?: string;
  showHeader?: boolean;
  collapseRight?: () => void;
  panelRef?: RefObject<HTMLDivElement>;
}

export const DesignTokensSkeleton: React.FC<DesignTokensSkeletonProps> =
  React.memo(({ className, showHeader = true, collapseRight, panelRef }) => {
    return (
      <div
        ref={panelRef}
        className={cn(
          "w-full h-full bg-background-primary border-r border-border-primary flex flex-col relative",
          className,
        )}
        id="design-tokens-panel-root"
      >
        {showHeader && collapseRight && (
          <DesignTokensPanelHeader collapseRight={collapseRight} />
        )}

        {/* Search and Controls Skeleton */}
        <div className="p-2 border-b border-[#2f3035] flex items-center gap-2 max-h-11 min-w-0">
          <div className="h-7 flex-1 min-w-0 bg-[#2f3035] rounded animate-pulse" />
          <div className="w-7 h-7 bg-[#2f3035] rounded animate-pulse" />
        </div>

        {/* Content Skeleton */}
        <div
          className="flex-1 overflow-y-auto hide-scrollbar p-2 min-w-0 max-w-full"
          style={{ boxSizing: "border-box" }}
        >
          <div className="space-y-2 min-w-0">
            {/* Skeleton ungrouped colors */}
            <div className="space-y-1 min-w-0">
              {[
                "skeleton-ungrouped-1",
                "skeleton-ungrouped-2",
                "skeleton-ungrouped-3",
              ].map((key) => (
                <div
                  key={key}
                  className="flex items-center px-2 py-1 rounded-md w-full"
                >
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-4 h-4 bg-[#2f3035] rounded animate-pulse" />
                    <div className="flex items-center gap-1 w-full min-w-0">
                      <div
                        className="h-3 bg-[#2f3035] rounded animate-pulse flex-1"
                        style={{ width: `${60 + Math.random() * 40}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Separator */}
            <div className="border-b border-[#2f3035] my-3" />

            {/* Skeleton grouped colors */}
            {["skeleton-group-1", "skeleton-group-2", "skeleton-group-3"].map(
              (groupKey) => (
                <div key={groupKey} className="flex flex-col w-full">
                  {/* Group header skeleton */}
                  <div className="flex items-center p-2 rounded-md group transition-colors text-left h-7 capitalize w-full">
                    <div className="flex items-center gap-1.5 h-[20px] flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-[#f5f5f6] text-xs font-normal transition-colors shrink-0">
                        <div className="w-4 h-4 bg-[#2f3035] rounded animate-pulse" />
                      </div>
                      <div className="flex-1 min-w-0 h-7 flex items-center">
                        <div
                          className="h-3 bg-[#2f3035] rounded animate-pulse"
                          style={{ width: `${50 + Math.random() * 30}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Group content skeleton */}
                  <div className="space-y-1 min-w-0 ml-4">
                    {groupKey === "skeleton-group-1"
                      ? ["skeleton-color-1-1", "skeleton-color-1-2"].map(
                          (colorKey) => (
                            <div
                              key={colorKey}
                              className="flex items-center px-2 py-1 rounded-md w-full"
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div className="w-4 h-4 bg-[#2f3035] rounded animate-pulse" />
                                <div className="flex items-center gap-1 w-full min-w-0">
                                  <div
                                    className="h-3 bg-[#2f3035] rounded animate-pulse flex-1"
                                    style={{
                                      width: `${70 + Math.random() * 30}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ),
                        )
                      : [
                          "skeleton-color-2-1",
                          "skeleton-color-2-2",
                          "skeleton-color-2-3",
                        ].map((colorKey) => (
                          <div
                            key={colorKey}
                            className="flex items-center px-2 py-1 rounded-md w-full"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <div className="w-4 h-4 bg-[#2f3035] rounded animate-pulse" />
                              <div className="flex items-center gap-1 w-full min-w-0">
                                <div
                                  className="h-3 bg-[#2f3035] rounded animate-pulse flex-1"
                                  style={{
                                    width: `${70 + Math.random() * 30}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    );
  });
